import { Group, MeshStandardMaterial } from 'three';

import type { SceneComposition, SceneObject } from '../../types/engine.types';
import type { SceneBuildManifest } from '../../types/scene-build-manifest.type';
import { BaseSceneBuilder } from '../scene-builder.interface';
import { GeometryBuilder } from '../geometry-builder';
import { KitchenDataManager } from '../../../utils/kitchen-data-manager';
import { TextureInfoManager } from '../../../utils/texture-info-manager';
import { TextureLoadingQueue } from '../../managers/texture-manager';

import type { KitchenBlueprintInput, KitchenSceneBuilderConfig } from './types';
import { DEFAULT_KITCHEN_CONFIG } from './types';
import { buildCfn } from './functions/build.cfn';
import { buildProgressiveCfn } from './functions/build-progressive.cfn';
import { buildSingleIslandCfn } from './functions/build-island.cfn';
import { regenerateCutoutsForIslandsCfn } from './functions/generate-cutouts.cfn';
import type { CutoutAdjust } from './functions/generate-cutouts.cfn';

/**
 * KitchenSceneBuilder converts kitchen blueprint data into scene compositions.
 *
 * This builder handles the complete kitchen rendering pipeline:
 * - Parsing kitchen blueprint data
 * - Creating island container groups
 * - Loading textures (surround, counter, floor, sky)
 * - Creating panel geometry (surrounds, trims, countertops)
 * - Loading appliance models
 * - Generating cutouts for appliances
 * - Camera positioning
 *
 * @example
 * ```typescript
 * const builder = new KitchenSceneBuilder({
 *   basePath: '/assets/',
 *   renderQuality: 7,
 *   qualitySettings: null,
 *   textureManager: engine.textureManager,
 *   modelManager: engine.modelManager,
 *   cutoutManager: engine.cutoutManager
 * });
 *
 * const composition = await builder.build({
 *   kitchen: props.kitchen,
 *   controller: props.controller,
 *   floorKey: props.floorKey
 * });
 *
 * await engine.applyComposition(composition);
 * ```
 */
export class KitchenSceneBuilder extends BaseSceneBuilder<KitchenBlueprintInput> {
	readonly name = 'kitchen';

	#config: KitchenSceneBuilderConfig;
	#kitchenDataManager: KitchenDataManager;
	#textureInfoManager: TextureInfoManager;
	#geometryBuilder: GeometryBuilder;
	#textureLoadingQueue: TextureLoadingQueue;
	#materials = new Map<string, MeshStandardMaterial>();
	#islandGroups: Group[] = [];
	#islandRenderOrder: Array<{ index: number; wave: number }> = [];
	#currentManifest: SceneBuildManifest | null = null;

	constructor(config: Partial<KitchenSceneBuilderConfig> = {}) {
		super();
		this.#config = { ...DEFAULT_KITCHEN_CONFIG, ...config };
		this.#kitchenDataManager = new KitchenDataManager();
		this.#textureInfoManager = new TextureInfoManager();
		this.#textureLoadingQueue = new TextureLoadingQueue({ maxConcurrent: 4 });

		// Configure the texture loading queue if textureManager is available
		if (this.#config.textureManager) {
			this.#textureLoadingQueue.configure(
				this.#config.textureManager,
				this.#config.basePath,
				this.#handleMaterialLoaded.bind(this)
			);
		}

		this.#geometryBuilder = new GeometryBuilder({
			basePath: this.#config.basePath,
			renderQuality: this.#config.renderQuality,
			qualitySettings: this.#config.qualitySettings,
			materials: this.#materials
		});
	}

	// ========== CFN Accessors ==========

	/** @internal Access to config for CFN functions */
	get configAccess(): KitchenSceneBuilderConfig {
		return this.#config;
	}

	/** @internal Access to kitchen data manager for CFN functions */
	get kitchenDataManagerAccess(): KitchenDataManager {
		return this.#kitchenDataManager;
	}

	/** @internal Access to texture info manager for CFN functions */
	get textureInfoManagerAccess(): TextureInfoManager {
		return this.#textureInfoManager;
	}

	/** @internal Access to geometry builder for CFN functions */
	get geometryBuilderAccess(): GeometryBuilder {
		return this.#geometryBuilder;
	}

	/** @internal Access to materials for CFN functions */
	get materialsAccess(): Map<string, MeshStandardMaterial> {
		return this.#materials;
	}

	/** @internal Access to island groups for CFN functions */
	get islandGroupsAccess(): Group[] {
		return this.#islandGroups;
	}

	/** @internal Access to island render order for CFN functions */
	get islandRenderOrderAccess(): Array<{ index: number; wave: number }> {
		return this.#islandRenderOrder;
	}

	/** @internal Access to texture loading queue for CFN functions */
	get textureLoadingQueueAccess(): TextureLoadingQueue {
		return this.#textureLoadingQueue;
	}

	/** @internal Current scene build manifest (set by buildCfn / buildProgressiveCfn) */
	get currentManifestAccess(): SceneBuildManifest | null {
		return this.#currentManifest;
	}

	/** @internal Set current scene build manifest */
	set currentManifestAccess(manifest: SceneBuildManifest | null) {
		this.#currentManifest = manifest;
	}

	/** @internal Clear island groups */
	clearIslandGroups(): void {
		this.#islandGroups = [];
	}

	/** @internal Add island group */
	addIslandGroup(group: Group): void {
		this.#islandGroups.push(group);
	}

	/** @internal Set island render order */
	setIslandRenderOrder(order: Array<{ index: number; wave: number }>): void {
		this.#islandRenderOrder = order;
	}

	/**
	 * Handle material loaded from texture queue.
	 * Registers the material in the materials map and updates GeometryBuilder.
	 * @internal
	 */
	#handleMaterialLoaded(key: string, material: MeshStandardMaterial): void {
		this.#materials.set(key, material);
		this.#geometryBuilder.setMaterials(this.#materials);
	}

	// ========== Public API ==========

	/**
	 * Update configuration
	 */
	updateConfig(config: Partial<KitchenSceneBuilderConfig>): void {
		this.#config = { ...this.#config, ...config };
		this.#geometryBuilder.updateConfig({
			basePath: this.#config.basePath,
			renderQuality: this.#config.renderQuality,
			qualitySettings: this.#config.qualitySettings
		});

		// Reconfigure texture loading queue if textureManager is updated
		if (config.textureManager || config.basePath) {
			this.#textureLoadingQueue.configure(
				this.#config.textureManager!,
				this.#config.basePath,
				this.#handleMaterialLoaded.bind(this)
			);
		}
	}

	/**
	 * Get island groups
	 */
	getIslandGroups(): Group[] {
		return this.#islandGroups;
	}

	/**
	 * Get kitchen data manager
	 */
	getKitchenDataManager(): KitchenDataManager {
		return this.#kitchenDataManager;
	}

	/**
	 * Build scene composition from kitchen blueprint
	 */
	async build(input: KitchenBlueprintInput): Promise<SceneComposition> {
		return buildCfn(this, { input });
	}

	/**
	 * Build scene progressively - yields objects as they're created.
	 * This allows for non-blocking scene display where objects appear incrementally.
	 *
	 * Yield order (per island, sequentially):
	 * 1. Floor (visible immediately)
	 * 2. For each island in sequence:
	 *    a. Island group container
	 *    b. Panels (type: 'panel')
	 *    c. Corners/trims (type: 'corner')
	 *    d. Countertop (type: 'countertop')
	 * 3. Cutouts (type: 'cutout')
	 * 4. Appliances (type: 'appliance')
	 *
	 * The final return value contains bounds and camera configuration.
	 */
	async *buildProgressive(input: KitchenBlueprintInput): AsyncGenerator<SceneObject, SceneComposition, void> {
		yield* buildProgressiveCfn(this, { input });
		// buildProgressiveCfn returns the final SceneComposition
		return {
			objects: [],
			bounds: { min: { x: 0, y: 0, z: 0 }, max: { x: 0, y: 0, z: 0 } },
			camera: { position: { x: 0, y: 0, z: 0 }, target: { x: 0, y: 0, z: 0 } }
		};
	}

	/**
	 * Build a single island by ID from current kitchen data.
	 * Reuses per-island build logic without touching other islands.
	 * Textures are expected to already be cached from the initial build.
	 *
	 * Returns the new island Group ready to be inserted into the scene, or null if not found.
	 */
	public async buildSingleIsland (islandId: string, input: KitchenBlueprintInput): Promise<Group | null> {
		return buildSingleIslandCfn(this, { islandId, input });
	}

	/**
	 * Regenerate cutouts on the current scene islands (used for incremental updates).
	 * This keeps existing appliances in place and updates only structural meshes.
	 */
	/**
	 * Regenerate ONE island's cutouts from the domain, optionally leaving some
	 * appliances' holes out or shifting them (the drag-and-drop preview).
	 * Nothing else in the island is rebuilt.
	 */
	public async regenerateCutoutsForIsland(islandGroup: Group, adjust?: CutoutAdjust): Promise<Group | null> {
		const islandId = islandGroup.userData?.id as string | undefined;
		const targetIslandIndex = this.kitchenDataManagerAccess.getIslands().findIndex((island: { id?: string }) => island.id === islandId);
		if (targetIslandIndex < 0) return null;
		return regenerateCutoutsForIslandsCfn(this, { islandGroups: [islandGroup], targetIslandIndex, adjust });
	}

	public async regenerateCutoutsForIslands(islandGroups: Group[]): Promise<Group | null> {
		return regenerateCutoutsForIslandsCfn(this, { islandGroups });
	}

	/**
	 * Clear builder state
	 */
	clear(): void {
		this.#islandGroups = [];
		this.#materials.clear();
		this.#textureLoadingQueue.clear();
	}

	/**
	 * Dispose of resources
	 */
	dispose(): void {
		this.clear();
		this.#geometryBuilder.dispose();
		this.#textureLoadingQueue.reset();
	}
}
